import { useQuery } from '@tanstack/react-query';
import { tenantManagementApi } from '../../api/tenantManagement';

export type DnsStatusType = 'idle' | 'loading' | 'connected' | 'need_setup' | 'error';

interface UseDnsStatusProps {
  organizationId: string;
  domain: string | undefined;
}

interface UseDnsStatusReturn {
  dnsStatus: DnsStatusType;
  refetchDns: () => void;
  error: string | null;
}

export const useDnsStatus = ({ organizationId, domain }: UseDnsStatusProps): UseDnsStatusReturn => {
  const {
    data: dnsQueryData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dns-status', organizationId, domain],
    queryFn: async () => {
      if (!domain) return null;
      const response = await tenantManagementApi.registerDomain({
        organisation_id: organizationId,
        domain: domain,
        check_only: true, // read-only DNS status — must not write/overwrite the mapping
      });
      return response.data?.results?.hosting?.dns_status;
    },
    enabled: !!domain,
    staleTime: 30000, // Cache for 30 seconds
    retry: false, // Don't retry on failure
  });

  // Derive DNS status from query result
  const dnsStatus: DnsStatusType = isLoading
    ? 'loading'
    : isError
      ? 'error'
      : dnsQueryData === 'configured'
        ? 'connected'
        : dnsQueryData === 'needs_setup'
          ? 'need_setup'
          : dnsQueryData === undefined || dnsQueryData === null
            ? 'idle'
            : 'need_setup';

  return {
    dnsStatus,
    refetchDns: refetch,
    error: isError ? (error instanceof Error ? error.message : 'Failed to check DNS status') : null,
  };
};
